import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/services/queryClient'
import { BrowserRouter } from 'react-router'
import { Toaster, ToastBar } from 'react-hot-toast'
import { notify } from '@/utils/notify'
import { DndProvider } from 'react-dnd'
import { TouchBackend } from 'react-dnd-touch-backend'
import { TabDragLayer } from './components/common/TabDragLayer'
import App from './App.tsx'
import './index.css'
// Initialize API client configuration
import './services/apiClient'
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/utils/chunkReload'

// Resource load failures on <script>/<link> don't bubble — capture phase is required.
window.addEventListener(
  'error',
  (event) => {
    const target = event.target
    if (!target || target === window) return
    const src =
      (target as HTMLScriptElement).src ||
      (target as HTMLLinkElement).href ||
      ''
    if (src && /[-.][a-zA-Z0-9]{6,12}\.(js|mjs|css)/.test(src)) {
      reloadOnceForStaleChunk(`asset load failed: ${src}`)
    }
  },
  true,
)

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    const reason = event.reason as { message?: unknown } | null
    const msg = reason && typeof reason.message === 'string' ? reason.message : String(event.reason)
    reloadOnceForStaleChunk(`unhandledrejection: ${msg}`)
  }
})

const toastStyle = {
  background: '#363636',
  color: '#fff',
}

const successIconTheme = {
  primary: '#4ade80',
  secondary: '#fff',
}

const errorIconTheme = {
  primary: '#ef4444',
  secondary: '#fff',
}

const successOptions = {
  duration: 3000,
  iconTheme: successIconTheme,
}

const errorOptions = {
  duration: 5000,
  iconTheme: errorIconTheme,
}

const toastOptions = {
  duration: 4000,
  style: toastStyle,
  success: successOptions,
  error: errorOptions,
} as const

// Custom toast render function extracted to avoid inline function in JSX
const renderToast = (t: Parameters<typeof ToastBar>[0]['toast']) => (
  <ToastBar
    toast={t}
    style={{
      ...t.style,
      maxWidth: '450px',
    }}
  >
    {({ icon, message }) => (
      <>
        {icon}
        <span style={{
          flex: 1,
          fontSize: '14px',
          lineHeight: '1.4',
          paddingRight: t.type !== 'loading' ? '8px' : '0',
        }}>{message}</span>
        {t.type !== 'loading' && (
          <button
            type="button"
            onClick={() => { notify.dismiss(t.id) }}
            style={{
              marginLeft: 'auto',
              marginRight: '8px',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '3px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s',
              minWidth: '24px',
              height: '24px',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
              e.currentTarget.style.color = '#ffffff'
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'
              e.currentTarget.style.color = '#9ca3af'
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'
            }}
            aria-label="Dismiss notification"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </>
    )}
  </ToastBar>
)

// TouchBackend uses mouse/pointer events instead of native HTML5 Drag and Drop.
// delayMouseStart: 0 — ChromeTabs handles direction gating via pointerTrackingRef.
// When a tab pointerdown occurs, canDrag returns false until direction is decided.
// For vertical (library drag), a synthetic mousedown restarts the TouchBackend gesture.
const touchBackendOptions = {
  enableMouseEvents: true,
  delayMouseStart: 0,
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <DndProvider backend={TouchBackend} options={touchBackendOptions}>
        <TabDragLayer />
        <App />
      </DndProvider>
      {/* Toaster moved outside DndProvider to prevent portal conflicts */}
      <Toaster
        position="top-right"
        containerStyle={{ top: 60 }}
        toastOptions={toastOptions}
      >
        {renderToast}
      </Toaster>
    </BrowserRouter>
  </QueryClientProvider>,
)
